'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  usePathname,
  useSearchParams,
} from '@wilkins-software/redwood-utils';
import type { BlogPostFrontmatter } from '../../utils/blog-content.util';
import {
  blogPostMatchesAuthorSlug,
  blogPostMatchesTagSlug,
  getBlogAuthorNameForSlug,
  getBlogTagLabelForSlug,
} from '../../utils/blog-filter.util';
import { BlogArticleCard } from './blog-article-card';
import {
  BLOG_PAGE_SIZE_OPTIONS,
  BlogListPagination,
  DEFAULT_BLOG_PAGE_SIZE,
  type BlogPageSize,
} from './blog-list-pagination';

const ALL_POSTS_FILTER = 'All posts';
const CATEGORY_SEARCH_PARAM = 'category';
const TAG_SEARCH_PARAM = 'tag';
const AUTHOR_SEARCH_PARAM = 'author';
const PAGE_SEARCH_PARAM = 'page';
const PER_PAGE_SEARCH_PARAM = 'perPage';
const BLOG_LIST_SECTION_ID = 'blog-list';

type BlogCategoryFilterProps = {
  posts: BlogPostFrontmatter[];
  categories: string[];
};

function parseBlogPageSize(rawPerPage: string | null): BlogPageSize {
  const parsedPerPage = Number(rawPerPage);
  if (
    BLOG_PAGE_SIZE_OPTIONS.includes(parsedPerPage as BlogPageSize)
  ) {
    return parsedPerPage as BlogPageSize;
  }
  return DEFAULT_BLOG_PAGE_SIZE;
}

function parseBlogPageNumber(
  rawPage: string | null,
  totalPages: number,
): number {
  const parsedPage = Number(rawPage);
  if (
    !Number.isInteger(parsedPage) ||
    parsedPage < 1 ||
    totalPages < 1
  ) {
    return 1;
  }
  return Math.min(parsedPage, totalPages);
}

function buildBlogListHref(
  pathname: string,
  searchParams: URLSearchParams,
): string {
  const query = searchParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function getBrowserHref(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Soft same-path query update so category/pagination changes stay shareable
 * without a full document navigation (rwsdk client navigation).
 * Uses pushState so Back restores the previous page/category/perPage.
 */
function pushBlogListSearchParams(href: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (getBrowserHref() === href) {
    return;
  }
  window.history.pushState(window.history.state, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function scrollBlogListSectionIntoView(
  blogListSectionElement: HTMLElement | null,
): void {
  if (blogListSectionElement == null || typeof window === 'undefined') {
    return;
  }
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  blogListSectionElement.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });
}

function formatSlugFallbackLabel(slug: string): string {
  return slug
    .split('-')
    .filter((segment) => segment.length > 0)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function BlogCategoryFilter({
  posts,
  categories,
}: BlogCategoryFilterProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const blogListSectionRef = useRef<HTMLDivElement>(null);
  const hasCommittedInitialListStateRef = useRef(false);

  const selectedCategory =
    searchParams.get(CATEGORY_SEARCH_PARAM) ?? ALL_POSTS_FILTER;
  const selectedTagSlug = searchParams.get(TAG_SEARCH_PARAM);
  const selectedAuthorSlug = searchParams.get(AUTHOR_SEARCH_PARAM);
  const perPage = parseBlogPageSize(
    searchParams.get(PER_PAGE_SEARCH_PARAM),
  );

  const filterLabels = [ALL_POSTS_FILTER, ...categories];
  const isKnownCategory =
    selectedCategory === ALL_POSTS_FILTER ||
    categories.includes(selectedCategory);
  const activeCategory = isKnownCategory
    ? selectedCategory
    : ALL_POSTS_FILTER;

  const activeTagLabel = useMemo(() => {
    if (!selectedTagSlug) {
      return undefined;
    }
    return (
      getBlogTagLabelForSlug(posts, selectedTagSlug) ??
      formatSlugFallbackLabel(selectedTagSlug)
    );
  }, [posts, selectedTagSlug]);

  const activeAuthorName = useMemo(() => {
    if (!selectedAuthorSlug) {
      return undefined;
    }
    return (
      getBlogAuthorNameForSlug(posts, selectedAuthorSlug) ??
      formatSlugFallbackLabel(selectedAuthorSlug)
    );
  }, [posts, selectedAuthorSlug]);

  const hasTagOrAuthorFilter =
    Boolean(selectedTagSlug) || Boolean(selectedAuthorSlug);

  const filteredPosts = useMemo(() => {
    let matchingPosts = posts;

    if (activeCategory !== ALL_POSTS_FILTER) {
      matchingPosts = matchingPosts.filter(
        (post) => post.category === activeCategory,
      );
    }

    if (selectedTagSlug) {
      matchingPosts = matchingPosts.filter((post) =>
        blogPostMatchesTagSlug(post, selectedTagSlug),
      );
    }

    if (selectedAuthorSlug) {
      matchingPosts = matchingPosts.filter((post) =>
        blogPostMatchesAuthorSlug(post, selectedAuthorSlug),
      );
    }

    return matchingPosts;
  }, [activeCategory, posts, selectedAuthorSlug, selectedTagSlug]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPosts.length / perPage),
  );
  const currentPage = parseBlogPageNumber(
    searchParams.get(PAGE_SEARCH_PARAM),
    filteredPosts.length === 0 ? 1 : totalPages,
  );

  const paginatedPosts = useMemo(() => {
    const pageStartIndex = (currentPage - 1) * perPage;
    return filteredPosts.slice(pageStartIndex, pageStartIndex + perPage);
  }, [currentPage, filteredPosts, perPage]);

  useEffect(() => {
    if (!hasCommittedInitialListStateRef.current) {
      hasCommittedInitialListStateRef.current = true;
      return;
    }
    scrollBlogListSectionIntoView(blogListSectionRef.current);
  }, [
    activeCategory,
    currentPage,
    perPage,
    selectedAuthorSlug,
    selectedTagSlug,
  ]);

  const updateBlogListParams = useCallback(
    (mutator: (nextSearchParams: URLSearchParams) => void) => {
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      mutator(nextSearchParams);
      pushBlogListSearchParams(
        buildBlogListHref(pathname, nextSearchParams),
      );
    },
    [pathname, searchParams],
  );

  const handleCategorySelect = useCallback(
    (categoryLabel: string) => {
      updateBlogListParams((nextSearchParams) => {
        if (categoryLabel === ALL_POSTS_FILTER) {
          nextSearchParams.delete(CATEGORY_SEARCH_PARAM);
        } else {
          nextSearchParams.set(CATEGORY_SEARCH_PARAM, categoryLabel);
        }
        nextSearchParams.delete(PAGE_SEARCH_PARAM);
      });
    },
    [updateBlogListParams],
  );

  const handleClearTagAndAuthorFilters = useCallback(() => {
    updateBlogListParams((nextSearchParams) => {
      nextSearchParams.delete(TAG_SEARCH_PARAM);
      nextSearchParams.delete(AUTHOR_SEARCH_PARAM);
      nextSearchParams.delete(PAGE_SEARCH_PARAM);
    });
  }, [updateBlogListParams]);

  const handleViewAllBlogPosts = useCallback(() => {
    updateBlogListParams((nextSearchParams) => {
      nextSearchParams.delete(CATEGORY_SEARCH_PARAM);
      nextSearchParams.delete(TAG_SEARCH_PARAM);
      nextSearchParams.delete(AUTHOR_SEARCH_PARAM);
      nextSearchParams.delete(PAGE_SEARCH_PARAM);
    });
  }, [updateBlogListParams]);

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      updateBlogListParams((nextSearchParams) => {
        if (pageNumber <= 1) {
          nextSearchParams.delete(PAGE_SEARCH_PARAM);
        } else {
          nextSearchParams.set(PAGE_SEARCH_PARAM, String(pageNumber));
        }
      });
    },
    [updateBlogListParams],
  );

  const handlePerPageChange = useCallback(
    (pageSize: BlogPageSize) => {
      updateBlogListParams((nextSearchParams) => {
        if (pageSize === DEFAULT_BLOG_PAGE_SIZE) {
          nextSearchParams.delete(PER_PAGE_SEARCH_PARAM);
        } else {
          nextSearchParams.set(PER_PAGE_SEARCH_PARAM, String(pageSize));
        }
        nextSearchParams.delete(PAGE_SEARCH_PARAM);
      });
    },
    [updateBlogListParams],
  );

  const blogListContentKey = `${activeCategory}-${selectedTagSlug ?? ''}-${selectedAuthorSlug ?? ''}-${currentPage}-${perPage}`;

  let emptyStateHeading = 'No blog posts found';
  let emptyStateDescription =
    'Try another filter or check back soon for deep-dives into architecture, scalability, and DevOps.';
  if (selectedTagSlug) {
    emptyStateHeading = 'No blog posts with this tag';
    emptyStateDescription =
      'Try another tag or browse all blog posts.';
  } else if (selectedAuthorSlug) {
    emptyStateHeading = 'No blog posts by this author';
    emptyStateDescription =
      'Try another author or browse all blog posts.';
  } else if (activeCategory !== ALL_POSTS_FILTER) {
    emptyStateHeading = 'No blog posts in this category';
  }

  return (
    <div
      ref={blogListSectionRef}
      id={BLOG_LIST_SECTION_ID}
      className="scroll-mt-20"
    >
      <section className="overflow-x-auto border-b border-slate-200 py-4">
        <div className="flex min-w-max items-center gap-6 whitespace-nowrap">
          {filterLabels.map((categoryLabel) => {
            const isActiveCategory = activeCategory === categoryLabel;
            return (
              <button
                key={categoryLabel}
                type="button"
                onClick={() => handleCategorySelect(categoryLabel)}
                className={
                  isActiveCategory
                    ? 'border-b-2 border-primary pb-2 text-sm font-bold text-primary transition-[color,border-color] duration-200 ease-out motion-reduce:transition-none'
                    : 'border-b-2 border-transparent pb-2 text-sm text-slate-600 transition-[color,border-color] duration-200 ease-out hover:text-primary motion-reduce:transition-none'
                }
              >
                {categoryLabel}
              </button>
            );
          })}
        </div>
      </section>

      {hasTagOrAuthorFilter ? (
        <section className="flex flex-wrap items-center gap-3 border-b border-slate-200 py-4">
          <p className="text-sm text-slate-600">
            {activeTagLabel ? (
              <>
                Posts tagged{' '}
                <span className="font-semibold text-gray-900">
                  {activeTagLabel}
                </span>
              </>
            ) : null}
            {activeTagLabel && activeAuthorName ? (
              <span className="text-slate-400"> · </span>
            ) : null}
            {activeAuthorName ? (
              <>
                Posts by{' '}
                <span className="font-semibold text-gray-900">
                  {activeAuthorName}
                </span>
              </>
            ) : null}
          </p>
          <button
            type="button"
            onClick={handleClearTagAndAuthorFilters}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-primary hover:text-primary"
          >
            Clear filter
          </button>
        </section>
      ) : null}

      <section className="py-8">
        {filteredPosts.length === 0 ? (
          <div
            key={blogListContentKey}
            className="flex animate-in fade-in slide-in-from-bottom-2 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm duration-300 ease-out motion-reduce:animate-none"
            style={{
              backgroundImage:
                'radial-gradient(#e2e8f0 0.5px, transparent 0.5px)',
              backgroundSize: '24px 24px',
            }}
          >
            <h3 className="text-2xl font-semibold tracking-tight text-gray-900">
              {emptyStateHeading}
            </h3>
            <p className="mt-2 max-w-md text-base text-slate-600">
              {emptyStateDescription}
            </p>
            {hasTagOrAuthorFilter ||
            activeCategory !== ALL_POSTS_FILTER ? (
              <button
                type="button"
                onClick={handleViewAllBlogPosts}
                className="mt-6 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-95"
              >
                View all blog posts
              </button>
            ) : null}
          </div>
        ) : paginatedPosts.length === 0 ? (
          <div
            key={blogListContentKey}
            className="flex animate-in fade-in slide-in-from-bottom-2 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm duration-300 ease-out motion-reduce:animate-none"
            style={{
              backgroundImage:
                'radial-gradient(#e2e8f0 0.5px, transparent 0.5px)',
              backgroundSize: '24px 24px',
            }}
          >
            <h3 className="text-2xl font-semibold tracking-tight text-gray-900">
              No blog posts on this page
            </h3>
            <p className="mt-2 max-w-md text-base text-slate-600">
              This page is empty. Head back to the first page to keep browsing.
            </p>
            <button
              type="button"
              onClick={() => handlePageChange(1)}
              className="mt-6 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:opacity-90 active:scale-95"
            >
              Go to page 1
            </button>
          </div>
        ) : (
          <>
            <div
              key={blogListContentKey}
              className="grid animate-in fade-in slide-in-from-bottom-2 grid-cols-1 gap-6 duration-300 ease-out motion-reduce:animate-none md:grid-cols-2 lg:grid-cols-3"
            >
              {paginatedPosts.map((post) => (
                <BlogArticleCard key={post.slug} post={post} />
              ))}
            </div>
            <BlogListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              totalFilteredPosts={filteredPosts.length}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )}
      </section>
    </div>
  );
}
